import type { ReactElement } from "react";

import { useDispatch } from "react-redux";

import { EmptyState } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { ErrorBoundaryWithLogging } from "app/pages/sales-intelligence/common-components/ErrorBoundaryWithLogging";
import {
    StyledLeadsBaseContentContainer,
    StyledLeadsTabContent,
    StyledLeadsTableWrapper,
} from "app/pages/sales-intelligence/common-components/layout";
import type { TabContentComponentProps } from "app/pages/sales-intelligence/common-components/tabbed-page-content";
import type { LeadsType, TabBase } from "app/pages/sales-intelligence/modules/leads-search";
import { useIsLeadsPanelDataLoading } from "app/pages/sales-intelligence/modules/leads-search/hooks/useIsLeadsPanelDataLoading";
import { LeadsSearchFiltersContainer } from "app/pages/sales-intelligence/modules/leads-search-filters";
import { useListParams } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import { createContactsResultsListTableColumns } from "app/pages/sales-intelligence/pages/opportunity-list/configuration/columns/contactsColumnsList";
import { useOpportunityListContactsFlowStrategy } from "app/pages/sales-intelligence/pages/opportunity-list/hooks/useOpportunityListContactsFlowStrategy";
import { useOpportunityListContactsResults } from "app/pages/sales-intelligence/pages/opportunity-list/hooks/useOpportunityListContactsResults";
import { exportSelectedContactsToExcelThunk } from "app/pages/sales-intelligence/pages/opportunity-list/store/effects";
import ContactsSearchResultsContainer from "app/pages/sales-intelligence/sub-modules/contacts-search/containers/ContactSearchResultContainer";
import { ContactsFlowProvider } from "app/pages/sales-intelligence/sub-modules/contacts-search/context/ContactsFlowContext";
import type { ContactsSearchResults } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/common";
import type { ContactIdCompanyPair } from "app/pages/sales-intelligence/sub-modules/contacts-search/types/dto";
import { useCurrentOpportunityListCountry } from "app/pages/sales-intelligence/sub-modules/opportunities/hooks/useCurrentOpportunityListCountry";

import ContactsTableActionsPanelContainer from "../table-actions-panel/ContactsTableActionsPanel";

type Props = TabContentComponentProps<TabBase<LeadsType>>;

const INIT_CONTACTS_SEARCH_RESULTS: ContactsSearchResults = {
    totalCount: 0,
    rows: [],
};

const ContactsTabContentContainer = ({ shouldRenderDeferredContent = true }: Props): ReactElement => {
    const dispatch = useDispatch();
    const contactsFlowStrategy = useOpportunityListContactsFlowStrategy();
    const { data, isFetching, isPending, isError, error, refetch } = useOpportunityListContactsResults();

    const listParams = useListParams();
    const country = useCurrentOpportunityListCountry();

    const isLeadsPanelDataLoading = useIsLeadsPanelDataLoading();

    const isContentLoading = isFetching || isPending || !shouldRenderDeferredContent;

    async function handleExcelExport(contactIdCompanyPair: ContactIdCompanyPair): Promise<void> {
        await dispatch(exportSelectedContactsToExcelThunk(listParams, [contactIdCompanyPair]));
    }

    return (
        <ContactsFlowProvider value={contactsFlowStrategy}>
            <StyledLeadsTabContent data-automation="tab-content">
                <StyledLeadsBaseContentContainer>
                    <LeadsSearchFiltersContainer isLoading={isLeadsPanelDataLoading || !shouldRenderDeferredContent} />
                </StyledLeadsBaseContentContainer>

                <StyledLeadsTableWrapper>
                    <ContactsSearchResultsContainer
                        isLoading={isContentLoading}
                        actionsPanel={<ContactsTableActionsPanelContainer />}
                        searchResults={data || INIT_CONTACTS_SEARCH_RESULTS}
                        isError={isContentLoading ? false : isError}
                        error={error}
                        crmCountries={[country]}
                        onExportItemToExel={handleExcelExport}
                        onRefetchData={() => refetch()}
                        createColumnsConfigFactory={createContactsResultsListTableColumns}
                    />
                </StyledLeadsTableWrapper>
            </StyledLeadsTabContent>
        </ContactsFlowProvider>
    );
};

// TODO the error boundary should be moved to the contacts table level (upon redesign)
const ContactsTabContentWithErrorBoundary = (props: Props) => (
    <ErrorBoundaryWithLogging
        fallback={
            <EmptyState
                title={translate("error.boundary.title")}
                iconName="no-data-tree"
            />
        }
    >
        <ContactsTabContentContainer {...props} />
    </ErrorBoundaryWithLogging>
);

export default ContactsTabContentWithErrorBoundary;
